import React, { Fragment, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import pluralize from 'pluralize';
import compose from 'lodash.flowright';
import { matchPath, withRouter } from 'react-router-dom';
import { graphql, useQuery, withApollo } from 'react-apollo';
import { connect } from 'react-redux';

import { mapProps } from '../../../../util/hocs';
import CheckoutCartItem from '../CheckoutCartItem';
import CheckoutCartAddress from './CheckoutCartAddress';
import CheckoutCartShippingOptions from './CheckoutCartShippingOptions';
import PriceBreakdownList from '../PriceBreakdownList';
import CheckoutCartPaymentPanel from './CheckoutCartPaymentPanel';
import getAccount from '../../../../apps/temp/graphql/queries/getAccount';
import {
  getCheckoutCart,
  getCheckoutCartCheckoutItems,
  getCheckoutCartTotalItems,
  getCheckoutCartTotalPrice,
  getCheckoutPreviousCart,
  checkoutCartRequiresAddress,
  getUnconfiguredAlbumPrintPackageCartItems,
  getUnconfiguredPurchasedPrintPackageCartItems,
  getUnconfiguredTaggedAlbumPrintPackageCartItems,
  checkoutCartRequiresPrepaidNotice,
  getCheckoutPrintPartnerShippingOptionId,
} from '../../redux/checkoutSelectors';
import {
  clearCart as clearCartAction,
  onCartItemRemove as onCartItemRemoveAction,
} from '../../redux/checkoutActions';
import { openAlert as openAlertAction } from '../../../../actions/modal-actions';
import { getCheckoutPriceQuery } from '../../../../apps/temp/graphql/queries/getCheckoutPrice';
import { checkout as checkoutMutation } from '../../../../graphql/mutations/checkout';
import CART_ITEM from '../../constants/cartItems';
import { CURRENCY } from '../../../../constants/currency';
import useCheckoutCart from './hooks/useCheckoutCart';
import { getErrorString } from '../../../../util/normalize-errors';
import PhoneAuthWithEmailFallback from '../../../../components/auth/PhoneAuthWithEmailFallback';
import { trackAuthEvent } from '../../../../apps/web-gallery/utilities/analytics';
import Spinner from '../../../../components/global/Spinner';
import CheckoutCartPendingPurchases from './CheckoutCartPendingPurchases';
import {
  cartHasSubscriptionItems,
  containSingleEventItems,
  containSingleVendorItems,
  containsTransactionsMissingPrepaidItems,
  getCartItemEventId,
  getFreeSubscriptionItemPricing,
} from './helpers';
import useFreeSubscriptionPromoCode from '../../../../hooks/useFreeSubscriptionPromoCode';
import FreeSubscriptionPromoBanner from '../../../../components/global/FreeSubscriptionPromoBanner';
import Alert from '../../../../components/global/Alert';
import usePurchasesPendingConfiguration from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasesPendingConfiguration/hooks/usePurchasesPendingConfiguration';
import { getAlbumMembershipGroupQuery } from '../../../../apps/temp/views/memberships/AlbumMembershipGroup/graphql/queries/getAlbumMembershipGroup';
import { AlbumFolderForHierarchyShippingSettings } from '../../../../graphql/queries/album-folder';
import useContributorBusinessCard from '../../../../components/global/ContributorBusinessCard/hooks/useContributorBusinessCard';
import { getAlbumMembershipGroupEntityAccount } from '../../../../apps/temp/views/memberships/AlbumMembershipGroup/helpers';
import { getWatermarkSettingsQuery } from '../../../../apps/temp/views/memberships/AlbumMembershipGroup/graphql/queries/getWatermarkSettings';
import { extractWatermarkSettings } from '../../../../util/watermark-settings';
import CheckoutPrintOrderServices from './CheckoutCartPrintOrderServices/CheckoutPrintOrderServices';
import {
  getAppliedDiscountLabel,
  isFreeShippingDiscount,
} from '../../../../components/global/DiscountBanner/helpers';
import { API_TYPE } from '../../../../constants/print-partners';

const propTypes = {
  className: PropTypes.string,
  account: PropTypes.shape({
    id: PropTypes.string,
    addresses: PropTypes.array,
  }),
  cart: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.oneOf(Object.values(CART_ITEM)),
      albumFolder: PropTypes.object,
      album: PropTypes.object,
      albumCustomPrint: PropTypes.object,
      shipping: PropTypes.bool,
    }),
  ).isRequired,
  checkoutItems: PropTypes.object,
  totalCartItems: PropTypes.number,
  computedPrice: PropTypes.shape({
    value: PropTypes.number,
    currency: PropTypes.oneOf(Object.values(CURRENCY)),
  }),
  checkout: PropTypes.func.isRequired,
  openAlert: PropTypes.func.isRequired,
  pricing: PropTypes.shape({
    total_price: PropTypes.object,
    total_shipping_cost: PropTypes.object,
    total_tax: PropTypes.object,
    total_discount_price: PropTypes.object,
  }),
  loadingPricing: PropTypes.bool,
  errorFetchingPricing: PropTypes.object,
  refetchPricing: PropTypes.func,
  requiresAddress: PropTypes.bool,
  requiredPrepaidNotice: PropTypes.bool,
  itemsPriceBreakdown: PropTypes.arrayOf(
    PropTypes.shape({
      checkout_item_client_id: PropTypes.string,
      price: PropTypes.object,
      discount_price: PropTypes.object,
    }),
  ),
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  clearCart: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  checkoutRequirements: PropTypes.arrayOf(
    PropTypes.shape({
      message: PropTypes.string.isRequired,
      condition: PropTypes.bool.isRequired,
      scrollElement: PropTypes.string,
    }).isRequired,
  ),
  onClose: PropTypes.func,
  client: PropTypes.shape({
    resetStore: PropTypes.func,
  }),
  authenticated: PropTypes.bool,
  unconfiguredAlbumPrintPackageCartItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
  unconfiguredPurchasedPrintPackageCartItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
  unconfiguredTaggedAlbumPrintPackageCartItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
  stripeModalProps: PropTypes.shape({
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  }),
  hierarchyItemId: PropTypes.string,
  isValidSingleShippingDate: PropTypes.bool,
  vendor: PropTypes.shape({
    id: PropTypes.string,
  }),
  printPartner: PropTypes.shape({
    id: PropTypes.string,
    api_type: PropTypes.oneOf(Object.values(API_TYPE)),
  }),
  printPartnerShippingOptionId: PropTypes.string,
  checkoutDisabled: PropTypes.bool,
  vendorEmail: PropTypes.string,
  shippingAlbumFolder: PropTypes.shape({
    start_date: PropTypes.string,
    discount_template: PropTypes.object,
  }),
  freeSubscriptionPromoCode: PropTypes.shape({
    code: PropTypes.string,
  }),
  loadingFreeSubscriptionPromoCode: PropTypes.bool,
};

const CheckoutCart = ({
  className,
  cart,
  checkoutItems,
  totalCartItems,
  account,
  computedPrice,
  checkout,
  openAlert,
  pricing,
  loadingPricing,
  refetchPricing,
  errorFetchingPricing,
  requiresAddress,
  requiredPrepaidNotice,
  location,
  itemsPriceBreakdown = [],
  clearCart,
  onSuccess,
  checkoutRequirements = [],
  onClose,
  client,
  authenticated,
  unconfiguredAlbumPrintPackageCartItems,
  unconfiguredPurchasedPrintPackageCartItems,
  unconfiguredTaggedAlbumPrintPackageCartItems,
  stripeModalProps,
  hierarchyItemId: defaultHierarchyItemId,
  isValidSingleShippingDate,
  vendor,
  printPartner,
  printPartnerShippingOptionId,
  checkoutDisabled,
  vendorEmail,
  shippingAlbumFolder,
  freeSubscriptionPromoCode,
  loadingFreeSubscriptionPromoCode = false,
}) => {
  const match = matchPath(location.pathname, {
    path: '/app/shutterbug/hierarchy-items/:hierarchyItemId',
    exact: false,
  });
  const consumersMatch = matchPath(location.pathname, {
    path: '/consumer/matches/:albumMembershipGroupId',
    exact: false,
  });

  const albumMembershipGroupId =
    match?.params?.hierarchyItemId ??
    consumersMatch?.params?.albumMembershipGroupId;

  const { data: { albumMembershipGroup } = {} } = useQuery(
    getAlbumMembershipGroupQuery,
    {
      variables: {
        id: albumMembershipGroupId,
      },
      skip: !albumMembershipGroupId,
      fetchPolicy: 'cache-first',
    },
  );

  const { data: { hierarchyItem } = {} } = useQuery(getWatermarkSettingsQuery, {
    variables: {
      id: albumMembershipGroupId,
    },
    skip: !albumMembershipGroupId || albumMembershipGroup,
    fetchPolicy: 'cache-first',
  });

  const vendorAccount = getAlbumMembershipGroupEntityAccount(
    albumMembershipGroup || hierarchyItem,
  );

  const currencyCountry = useMemo(() => {
    if (pricing?.total_price?.currency === CURRENCY.CAD) {
      return 'CAN';
    }
    if (pricing?.total_price?.currency === CURRENCY.AUD) {
      return 'AUS';
    }
    return 'USA';
  }, [pricing]);

  const watermarkSettings = useMemo(
    () => extractWatermarkSettings(vendorAccount),
    [vendorAccount],
  );

  let hierarchyItemId = defaultHierarchyItemId;

  if (!hierarchyItemId) {
    if (albumMembershipGroup) {
      if (albumMembershipGroup.is_album) {
        hierarchyItemId = albumMembershipGroup.album.root_album_folder?.id;
      } else {
        hierarchyItemId = albumMembershipGroup.id;
      }
    } else if (hierarchyItem?.album) {
      hierarchyItemId = hierarchyItem?.album?.root_album_folder?.id;
    } else {
      hierarchyItemId = hierarchyItem?.album_folder?.root?.id;
    }
  }

  const hasNonOptionalUnconfiguredTaggedAlbumPrintPackageCartItems = (() => {
    if (
      !unconfiguredTaggedAlbumPrintPackageCartItems ||
      unconfiguredTaggedAlbumPrintPackageCartItems?.length === 0
    ) {
      return false;
    }

    return unconfiguredTaggedAlbumPrintPackageCartItems.some(cartItem => {
      const printPackageItemWithOverrides =
        cartItem.albumPrintPackage.print_package.items.records.find(
          printPackageItem =>
            printPackageItem.album_folder_print_package_items?.total > 0 &&
            !!(
              printPackageItem.album_folder_print_package_items?.records ?? []
            ).find(
              item =>
                item.album_folder_print_package.album_folder.id ===
                cartItem.albumPrintPackage.album_folder?.id,
            ),
        );

      return (
        !!printPackageItemWithOverrides &&
        !printPackageItemWithOverrides?.is_optional
      );
    });
  })();

  const classes = classNames({
    [className]: !!className,
    'checkout-cart': true,
  });

  const {
    prepaidPrintPackagesMissingConfiguration,
    prepaidPhotoPricingsMissingConfiguration,
    prepaidCustomPrintsMissingConfiguration,
  } = usePurchasesPendingConfiguration({
    hierarchyItemId,
    // Downloads are not shippable, so they must not gate the checkout.
    shippableOnly: true,
  });

  const { name: vendorName, photoAttributionPage: vendorAttribution } =
    useContributorBusinessCard({ accountId: vendor?.id });

  const { onCheckout, allowedPromoCodes, submitting } = useCheckoutCart({
    mutation: checkout,
    onSuccess: () => {
      // We clear the cart on success, as avoiding to do so will mean checkoutPrice will be recalled with
      // these items which could fail if we have Photo Download types, since these are blocked from re-purchase.
      clearCart();
      onSuccess?.({ cart });
    },
    onError: error => {
      openAlert({
        message: getErrorString(error),
        info: error?.request_id || '',
      });
    },
    openAlert,
    pricing,
    cart,
    checkoutItems,
    loadingPricing,
    refetchPricing,
    errorFetchingPricing,
  });

  useEffect(() => {
    if (totalCartItems === 0) {
      onClose?.();
    }
  }, [totalCartItems]);

  if (!authenticated) {
    return (
      <div className="p-4">
        <PhoneAuthWithEmailFallback
          introText={
            'In order to complete a purchase we need your phone number. ' +
            'Waldo will text a verification code to the number below.'
          }
          onAuthSuccess={authType => {
            client.resetStore();
            trackAuthEvent(authType);
          }}
          defaultEmailStep="LOGIN"
        />
      </div>
    );
  }

  if (!account) {
    return (
      <div
        data-testid="loading-spinner"
        className="w-100 d-flex align-items-center justify-content-center p-4"
      >
        <Spinner />
      </div>
    );
  }

  const [address] = account?.addresses ?? [];
  const containSingleVendorItemsBool = containSingleVendorItems(cart);
  const containSingleEventItemsBool = containSingleEventItems(cart);
  const containsTransactionsMissingPrepaidItemsBool =
    containsTransactionsMissingPrepaidItems(
      cart,
      prepaidPrintPackagesMissingConfiguration,
      prepaidPhotoPricingsMissingConfiguration,
      prepaidCustomPrintsMissingConfiguration,
    );

  const requirements = [
    ...checkoutRequirements,
    {
      message: (
        <span>
          This event has been closed by the photographer. For any questions
          please contact{' '}
          {vendorEmail ? (
            <a href={`mailto:${vendorEmail}`}>{vendorEmail}</a>
          ) : (
            'the photographer'
          )}
          .
        </span>
      ),
      condition: !checkoutDisabled,
    },
    {
      message: 'Please add a shipping address.',
      condition: !(requiresAddress && !address && !isValidSingleShippingDate),
      scrollElement: 'checkout-shipping-address',
    },
    {
      message: `Please add a shipping address within the same country as your vendor. (${currencyCountry})`,
      condition: !(
        requiresAddress &&
        !!address &&
        !isValidSingleShippingDate &&
        !!pricing?.total_price?.currency &&
        address?.country?.iso3 !== currencyCountry
      ),
      scrollElement: 'checkout-shipping-address',
    },
    {
      message: 'Some items in your cart require you to complete configuration.',
      condition:
        (unconfiguredAlbumPrintPackageCartItems?.length ?? 0) === 0 &&
        (unconfiguredPurchasedPrintPackageCartItems?.length ?? 0) === 0 &&
        ((unconfiguredTaggedAlbumPrintPackageCartItems?.length ?? 0) === 0 ||
          !hasNonOptionalUnconfiguredTaggedAlbumPrintPackageCartItems),
    },
    {
      message:
        'You have items that belong to multiple vendors. Ensure you only select items from a single seller in order to continue.',
      condition: containSingleVendorItemsBool,
    },
    {
      message:
        'You have items that belong to multiple events. Ensure you only select items from a single event in order to continue.',
      condition: containSingleEventItemsBool,
    },
    {
      message:
        'Items pre-purchased at the same time must be checked out within a single transaction',
      condition: !containsTransactionsMissingPrepaidItemsBool,
    },
    {
      message: 'All pre-purchased items must be configured before proceeding',
      condition:
        prepaidPrintPackagesMissingConfiguration.length === 0 &&
        prepaidPhotoPricingsMissingConfiguration.length === 0 &&
        prepaidCustomPrintsMissingConfiguration.length === 0,
    },
  ];

  const totalComputedPrice =
    computedPrice.value +
    (pricing?.total_shipping_cost.value ?? 0) +
    (pricing?.total_tax.value ?? 0);

  const discountAlbumFolder =
    albumMembershipGroup?.album_folder ??
    albumMembershipGroup?.album?.root_album_folder ??
    shippingAlbumFolder;
  const discountTemplate = discountAlbumFolder?.discount_template;
  const discountLabel = getAppliedDiscountLabel(discountTemplate, {
    eventStartDate: discountAlbumFolder?.start_date,
  });
  // The platform's `total_discount_price` leaves the Digital Photo Pass out, so
  // the amount saved comes from the same comparison the footer makes: the
  // cart's own pre-discount sum against the discounted subtotal.
  const discountedSubTotal = pricing
    ? pricing.total_price.value -
      (pricing.total_tax?.value ?? 0) -
      (pricing.total_shipping_cost?.value ?? 0)
    : 0;
  const discountSavings = pricing
    ? Math.max(0, computedPrice.value - discountedSubTotal)
    : 0;
  // Free shipping leaves no amount behind to show, so it is inferred from the
  // gallery's discount. Self-fulfilled orders never charge shipping, hence the
  // extra guards before claiming a discount.
  const hasFreeShippingDiscount =
    !!discountLabel &&
    isFreeShippingDiscount(discountTemplate) &&
    requiresAddress &&
    !isValidSingleShippingDate &&
    !!printPartner?.api_type &&
    printPartner.api_type !== API_TYPE.SELF_FULFILLMENT &&
    pricing?.total_shipping_cost?.value === 0 &&
    computedPrice.value >= (discountTemplate?.min_checkout_amount?.value ?? 0);
  // Only ever announce a discount the order actually received.
  const appliedDiscountLabel =
    discountSavings > 0 || hasFreeShippingDiscount ? discountLabel : null;

  return (
    <Fragment>
      <CheckoutCartPendingPurchases
        hierarchyItemId={hierarchyItemId}
        watermarkSettings={watermarkSettings}
      />
      <div className={classes}>
        <FreeSubscriptionPromoBanner
          promoCode={freeSubscriptionPromoCode}
          autoApplied
          className="mb-2"
        />
        <div className="d-flex align-items-center mb-2">
          <h3 className="m-0 text-extra-bold text-faded">Order Summary</h3>

          <span className="text-sm ml-auto text-faded text-bold">
            {totalCartItems} {pluralize('item', totalCartItems)}
          </span>
        </div>

        <div className="mb-4">
          {cart.map(item => {
            const price =
              itemsPriceBreakdown.find(
                priceBreakdown =>
                  priceBreakdown.checkout_item_client_id === item.id,
              ) ?? getFreeSubscriptionItemPricing(item, pricing);

            return (
              <CheckoutCartItem
                key={item.id}
                item={item}
                loading={loadingPricing}
                pricing={price}
                watermarkSettings={watermarkSettings}
                freePassPromoCode={freeSubscriptionPromoCode?.code}
              />
            );
          })}
          {!isValidSingleShippingDate && (
            <CheckoutPrintOrderServices
              albumMembershipGroup={albumMembershipGroup}
              itemsPriceBreakdown={itemsPriceBreakdown}
            />
          )}
        </div>

        {requiresAddress && !isValidSingleShippingDate && (
          <CheckoutCartAddress
            account={account}
            checkoutItems={checkoutItems}
            addressRequired
          />
        )}
        {requiresAddress && !isValidSingleShippingDate && (
          <CheckoutCartShippingOptions
            account={account}
            addressId={address?.id}
            checkoutItems={checkoutItems}
            printPartner={printPartner}
            vendor={vendor}
          />
        )}
        {requiresAddress && isValidSingleShippingDate && (
          <Alert type="blue-mid-light" className="mb-2" borderless>
            Your order will be directly delivered to the address chosen by{' '}
            <b>{vendorName}</b>.
            {vendorAttribution?.email_address && (
              <Fragment>
                <br />
                <br />
                Have any questions? Email them at{' '}
                <a
                  href={`mailto:${vendorAttribution.email_address}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {vendorAttribution.email_address}
                </a>
              </Fragment>
            )}
          </Alert>
        )}

        {requiredPrepaidNotice && (
          <Fragment>
            <h3 className="text-extra-bold text-faded">Reminder</h3>
            <Alert type="blue-mid-light" className="mb-2" borderless>
              This is a pre-order. After your proofs are delivered from this
              event, you will need to select your preferred image(s) for this
              order before it can be finalized and delivered.
            </Alert>
          </Fragment>
        )}

        {cart.length > 0 && (
          <Fragment>
            {pricing?.total_price.value > 0 && (
              <Fragment>
                <h3 className="text-extra-bold text-faded">Total</h3>
                <div className="rounded-box bg-gray-lightest">
                  <PriceBreakdownList
                    pricing={pricing}
                    loading={loadingPricing}
                    discount={discountSavings}
                    discountLabel={discountLabel}
                    freeShipping={hasFreeShippingDiscount}
                  />
                </div>
              </Fragment>
            )}

            {cart.length > 0 && (
              <CheckoutCartPaymentPanel
                discountLabel={appliedDiscountLabel}
                account={account}
                location={location}
                loading={loadingPricing}
                currency={pricing?.total_price.currency}
                finalPrice={pricing?.total_price.value}
                totalPrice={totalComputedPrice}
                onStripeToken={onCheckout}
                submitting={submitting}
                blockers={requirements.filter(
                  requirement => !requirement.condition,
                )}
                checkoutItems={checkoutItems}
                printPartnerShippingOptionId={printPartnerShippingOptionId}
                allowedPromoCodes={allowedPromoCodes}
                defaultPromoCode={freeSubscriptionPromoCode?.code}
                loadingDefaultPromoCode={loadingFreeSubscriptionPromoCode}
                stripeModalProps={stripeModalProps}
              />
            )}
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};

CheckoutCart.propTypes = propTypes;

const mapStateToProps = state => ({
  cart: getCheckoutCart(state),
  previousCart: getCheckoutPreviousCart(state),
  totalCartItems: getCheckoutCartTotalItems(state),
  checkoutItems: getCheckoutCartCheckoutItems(state),
  computedPrice: getCheckoutCartTotalPrice(state),
  requiresAddress: checkoutCartRequiresAddress(state),
  requiredPrepaidNotice: checkoutCartRequiresPrepaidNotice(state),
  selectedPrintPartnerShippingOptionId:
    getCheckoutPrintPartnerShippingOptionId(state),
  authenticated: state.auth.authenticated,
  unconfiguredAlbumPrintPackageCartItems:
    getUnconfiguredAlbumPrintPackageCartItems(state),
  unconfiguredPurchasedPrintPackageCartItems:
    getUnconfiguredPurchasedPrintPackageCartItems(state),
  unconfiguredTaggedAlbumPrintPackageCartItems:
    getUnconfiguredTaggedAlbumPrintPackageCartItems(state),
});

const mapDispatchToProps = {
  onCartItemRemove: onCartItemRemoveAction,
  clearCart: clearCartAction,
  openAlert: openAlertAction,
};

const getAlbumMembershipGroup = graphql(getAlbumMembershipGroupQuery, {
  options: ({ location }) => {
    const match = matchPath(location.pathname, {
      path: '/app/shutterbug/hierarchy-items/:hierarchyItemId',
      exact: false,
    });
    const consumersMatch = matchPath(location.pathname, {
      path: '/consumer/matches/:albumMembershipGroupId',
      exact: false,
    });

    const albumMembershipGroupId =
      match?.params?.hierarchyItemId ??
      consumersMatch?.params?.albumMembershipGroupId;

    return {
      variables: {
        id: albumMembershipGroupId,
      },
      fetchPolicy: 'cache-first',
    };
  },
  skip: ({ location }) => {
    const match = matchPath(location.pathname, {
      path: '/app/shutterbug/hierarchy-items/:hierarchyItemId',
      exact: false,
    });
    const consumersMatch = matchPath(location.pathname, {
      path: '/consumer/matches/:albumMembershipGroupId',
      exact: false,
    });

    const albumMembershipGroupId =
      match?.params?.hierarchyItemId ??
      consumersMatch?.params?.albumMembershipGroupId;

    return !albumMembershipGroupId;
  },
});

const getAlbumFolderHierarchyShipping = graphql(
  AlbumFolderForHierarchyShippingSettings,
  {
    options: ({ hierarchyItemId, albumFolderId }) => {
      return {
        variables: {
          id: hierarchyItemId ?? albumFolderId,
        },
        fetchPolicy: 'cache-first',
      };
    },
    skip: ({ hierarchyItemId, albumFolderId }) =>
      !hierarchyItemId && !albumFolderId,
  },
);

// Resolved ahead of pricing so the cart's totals already reflect the free pass
// code that the Stripe modal will auto-apply.
const withFreeSubscriptionPromoCode = BaseComponent => props => {
  const { cart, hierarchyItemId } = props;

  // The code belongs to the event of the pass being bought, which is not
  // always the event in the URL (e.g. cart opened from the folders list).
  const subscriptionCartItem = cart.find(item =>
    cartHasSubscriptionItems([item]),
  );
  const { promoCode, loading } = useFreeSubscriptionPromoCode({
    albumFolderId: getCartItemEventId(subscriptionCartItem) ?? hierarchyItemId,
    enabled: !!subscriptionCartItem,
  });

  return (
    <BaseComponent
      {...props}
      freeSubscriptionPromoCode={promoCode}
      loadingFreeSubscriptionPromoCode={loading}
    />
  );
};

const getCheckoutPrice = graphql(getCheckoutPriceQuery, {
  options: ({
    account,
    checkoutItems,
    isValidSingleShippingDate,
    printPartnerShippingOptionId,
    freeSubscriptionPromoCode,
  }) => {
    const [address] = account.addresses;

    return {
      variables: {
        account_id: account.id,
        address_id: isValidSingleShippingDate ? undefined : address?.id,
        print_partner_shipping_option_id: printPartnerShippingOptionId,
        promo_code: freeSubscriptionPromoCode?.code,
        ...checkoutItems,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    };
  },
  // Waits for the free pass code so the total does not jump once it applies.
  skip: ({ account, cart, loadingFreeSubscriptionPromoCode }) =>
    !account || !cart.length || loadingFreeSubscriptionPromoCode,
});

const withCheckoutMutation = graphql(checkoutMutation, {
  props: ({
    mutate,
    ownProps: {
      account,
      checkoutItems,
      isValidSingleShippingDate,
      printPartnerShippingOptionId,
    },
  }) => ({
    checkout: ({
      stripeTokenId,
      stripePaymentMethodId,
      email,
      promoCode,
      stripeConnectAccountId,
    }) =>
      mutate({
        variables: {
          input: {
            account_id: account.id,
            account_address_id: isValidSingleShippingDate
              ? undefined
              : account.addresses?.[0]?.id,
            stripe_token: stripeTokenId,
            email_address: email,
            promo_code: promoCode,
            platform: 'WEB',
            stripe_payment_method_id: stripePaymentMethodId,
            stripe_connect_account_id: stripeConnectAccountId,
            print_partner_shipping_option_id: printPartnerShippingOptionId,
            ...checkoutItems,
          },
        },
      }),
  }),
});

export { CheckoutCart as CheckoutCartComponent };

export default compose(
  withRouter,
  withApollo,
  connect(mapStateToProps, mapDispatchToProps),
  getAccount,
  mapProps(({ data, ...props }) => ({
    account: data?.me,
    ...props,
  })),
  getAlbumMembershipGroup,
  mapProps(({ data, ...props }) => ({
    albumMembershipGroup: data?.albumMembershipGroup,
    hierarchyItemId: data?.albumMembershipGroup?.is_album
      ? data?.albumMembershipGroup.album.root_album_folder?.id
      : data?.albumMembershipGroup?.id,
    ...props,
  })),
  getAlbumFolderHierarchyShipping,
  mapProps(({ data, ...props }) => {
    const { albumFolder } = data ?? {};
    const expiresAt = albumFolder?.hierarchy_shipping_settings?.expires_at
      ? new Date(albumFolder?.hierarchy_shipping_settings.expires_at)
      : null;
    const isValidSingleShippingDate =
      !!albumFolder?.hierarchy_shipping_settings &&
      (!expiresAt || expiresAt > new Date());
    const printPartnerShippingOptionId =
      props.requiresAddress && !isValidSingleShippingDate
        ? (props.selectedPrintPartnerShippingOptionId ?? undefined)
        : undefined;

    return {
      isValidSingleShippingDate,
      vendor: albumFolder?.account,
      printPartner: albumFolder?.print_partner,
      printPartnerShippingOptionId,
      shippingAlbumFolder: albumFolder,
      checkoutDisabled: albumFolder?.checkout_disabled,
      vendorEmail: albumFolder?.account?.email_address,
      ...props,
    };
  }),
  withFreeSubscriptionPromoCode,
  getCheckoutPrice,
  mapProps(({ data, ...props }) => ({
    pricing: data?.checkoutPrice,
    loadingPricing: data?.loading || props.loadingFreeSubscriptionPromoCode,
    refetchPricing: data?.refetch,
    errorFetchingPricing: data?.error,
    itemsPriceBreakdown: [
      ...(data?.checkoutPrice?.items_price_breakdown?.photo_items ?? []),
    ],
    ...props,
  })),
  withCheckoutMutation,
)(CheckoutCart);
